import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  Eye,
  Clock3,
  CheckCircle2,
  Code2,
  FileText,
} from 'lucide-react';
import { GlassContainer } from './GlassContainer';
import { TSizes } from '../constants/sizes';

export const ProjectDetailsDialog = ({
  project,
  open,
  onClose,
}) => {
  const [selectedMediaIndex, setSelectedMediaIndex] =
    useState(0);
  const [viewportWidth, setViewportWidth] = useState(
    () => window.innerWidth
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () =>
      window.removeEventListener(
        'resize',
        handleResize
      );
  }, []);

  const isMobile = viewportWidth < 820;
  const isTablet =
    viewportWidth >= 820 && viewportWidth < 1280;
  const isDesktop = viewportWidth >= 1280;

  const mediaUrls = useMemo(() => {
    const urls = [];

    if (project?.cover?.url) {
      urls.push(project.cover.url);
    }

    (project?.media || []).forEach((item) => {
      if (item?.url && !urls.includes(item.url)) {
        urls.push(item.url);
      }
    });

    return urls;
  }, [project]);

  useEffect(() => {
    setSelectedMediaIndex(0);
  }, [project]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [open, onClose]);

  if (!open || !project) return null;

  const selectedImage =
    mediaUrls[selectedMediaIndex] || '';

  return (
    <>
      <style>
        {`
          .project-dialog-scroll::-webkit-scrollbar {
            width: 7px;
          }

          .project-dialog-scroll::-webkit-scrollbar-thumb {
            background: var(--divider);
            border-radius: 999px;
          }

          .project-thumbs-scroll::-webkit-scrollbar {
            display: none;
          }

          .project-thumbs-scroll {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        `}
      </style>

      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: isDesktop
            ? '40px'
            : isTablet
              ? '22px'
              : '0',
        }}
      >
        <div
          onClick={onClose}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
          }}
        />

        <GlassContainer
          radius={isMobile ? 28 : TSizes.radius2XL || 32}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: isDesktop
              ? '1120px'
              : isTablet
                ? '980px'
                : '100%',
            height: isMobile ? '92vh' : 'auto',
            maxHeight: isMobile ? '92vh' : '88vh',
            overflow: 'hidden',
            marginRight: isMobile ? '20px' : '0',
            marginLeft: isMobile ? '20px' : '0',
            marginBottom: isMobile ? '50px' : '0',

          }}
        >
          <div
            className="project-dialog-scroll"
            style={{
              overflowY: 'auto',
              maxHeight: isMobile ? '92vh' : '88vh',
              padding: isDesktop
                ? '28px'
                : isTablet
                  ? '22px'
                  : '18px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                  ? 'auto 1fr auto'
                  : isTablet
                    ? 'auto 1fr auto'
                    : 'auto 1fr auto auto auto',
                alignItems: 'center',
                // gap: isMobile ? '10px' : '14px',
              }}
            >
              <button
                onClick={onClose}
                style={{
                  width: isMobile ? '38px' : '44px',
                  height: isMobile ? '38px' : '44px',
                  borderRadius: '999px',
                  border: 'none',
                  background: 'var(--nav-indicator)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--icon-primary)',
                  flexShrink: 0,
                }}
              >
                <X size={isMobile ? 18 : 20} />
              </button>

              {/* <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isMobile ? 'flex-start' : 'center',
                  minWidth: 0,
                }}
              >
                <MetricBadge
                  icon={<Eye size={14} />}
                  text={`${project?.views || 0}`}
                  compact={isMobile}
                />
              </div> */}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '10px' : '12px',
                  justifySelf: 'end',
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    minWidth: 0,
                    textAlign: isMobile ? 'right' : 'right',
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      color: 'var(--text-primary)',
                      fontSize: isDesktop
                        ? '22px'
                        : isTablet
                          ? '20px'
                          : '17px',
                      fontWeight: 900,
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {project?.title}
                  </h2>

                  <p
                    style={{
                      margin: '5px 0 0 0',
                      color: 'var(--text-secondary)',
                      fontSize: isMobile ? '12px' : '13px',
                      fontWeight: 600,
                    }}
                  >
                    Project Details
                  </p>
                </div>

                <div
                  style={{
                    width: isMobile ? '42px' : '52px',
                    height: isMobile ? '42px' : '52px',
                    borderRadius: '999px',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={project?.logo?.url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              </div>

              {/* {!isMobile && (
                <div
                  style={{
                    justifySelf: 'end',
                  }}
                >
                  <MetricBadge
                    icon={<Clock3 size={14} />}
                    text={`${project?.numberOfMonths || 0} mo`}
                    compact={false}
                  />
                </div>
              )} */}
            </div>

            <div style={{ height: isMobile ? '18px' : '26px' }} />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isDesktop ? '1.1fr 0.9fr' : '1fr',
                gap: isDesktop ? '32px' : '22px',
                alignItems: 'start',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    overflow: 'hidden',
                    borderRadius: `${isMobile ? 20 : TSizes.radiusXL || 24}px`,
                  }}
                >
                  <img
                    src={selectedImage}
                    alt=""
                    style={{
                      width: '100%',
                      height: isDesktop
                        ? '450px'
                        : isTablet
                          ? '370px'
                          : '250px',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </div>

                {mediaUrls.length > 1 && (
                  <div
                    className="project-thumbs-scroll"
                    style={{
                      marginTop: isMobile ? '12px' : '16px',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: isMobile ? '8px' : '10px',
                        width: 'max-content',
                        paddingBottom: '2px',
                      }}
                    >
                      {mediaUrls.map((url, index) => {
                        const selected =
                          selectedMediaIndex === index;

                        return (
                          <GlassContainer
                            key={index}
                            clickable
                            radius={isMobile ? 16 : 18}
                            style={{
                              width: isMobile ? '78px' : '94px',
                              minWidth: isMobile ? '78px' : '94px',
                              padding: '3px',
                              opacity: selected ? 1 : 0.55,
                              cursor: 'pointer',
                              border: selected
                                ? '1px solid var(--primary)'
                                : '1px solid rgba(255,255,255,0.12)',
                            }}
                          >
                            <div
                              onClick={() =>
                                setSelectedMediaIndex(index)
                              }
                            >
                              <img
                                src={url}
                                alt=""
                                style={{
                                  width: '100%',
                                  height: isMobile ? '58px' : '70px',
                                  objectFit: 'cover',
                                  borderRadius: isMobile ? '13px' : '15px',
                                  display: 'block',
                                }}
                              />
                            </div>
                          </GlassContainer>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ minWidth: 0 }}>
                <InfoSection
                  title="Overview"
                  icon={<FileText size={16} />}
                >
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--text-secondary)',
                      lineHeight: 1.8,
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: 600,
                    }}
                  >
                    {project?.bio}
                  </p>
                </InfoSection>

                <div style={{ height: '16px' }} />

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? '1fr 1fr'
                      : 'repeat(2,minmax(0,1fr))',
                    gap: '12px',
                  }}
                >
                  <MetaCard
                    icon={<Eye size={16} />}
                    label="Views"
                    value={`${project?.views || 0}+`}
                  />

                  <MetaCard
                    icon={<Clock3 size={16} />}
                    label="Duration"
                    value={`${project?.numberOfMonths || 0} mo`}
                  />

                  <MetaCard
                    icon={<CheckCircle2 size={16} />}
                    label="Features"
                    value={`${project?.projectFeatures?.length || 0}`}
                  />

                  <MetaCard
                    icon={<Code2 size={16} />}
                    label="Tools"
                    value={`${project?.projectTools?.length || 0}`}
                  />
                </div>

                {project?.projectFeatures?.length > 0 && (
                  <>
                    <div style={{ height: '16px' }} />

                    <InfoSection
                      title="Key Features"
                      icon={<CheckCircle2 size={16} />}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: isMobile ? '12px' : '10px',
                        }}
                      >
                        {project.projectFeatures.map(
                          (feature, index) => (
                            <BulletItem
                              key={index}
                              icon={<CheckCircle2 size={13} />}
                              text={feature?.title}
                            />
                          )
                        )}
                      </div>
                    </InfoSection>
                  </>
                )}

                {project?.projectTools?.length > 0 && (
                  <>
                    <div style={{ height: '16px' }} />

                    <InfoSection
                      title="Tools & Technologies"
                      icon={<Code2 size={16} />}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: isMobile ? '12px' : '10px',
                        }}
                      >
                        {project.projectTools.map((tool, index) => (
                          <BulletItem
                            key={index}
                            icon={<Code2 size={13} />}
                            text={tool?.title}
                          />
                        ))}
                      </div>
                    </InfoSection>
                  </>
                )}
              </div>
            </div>
          </div>
        </GlassContainer>
      </div>
    </>
  );
};

const MetricBadge = ({ icon, text, compact = false }) => {
  return (
    <GlassContainer
      radius={999}
      style={{
        padding: compact ? '7px 10px' : '8px 12px',
        width: 'fit-content',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: compact ? '6px' : '7px',
          color: 'var(--primary)',
          whiteSpace: 'nowrap',
        }}
      >
        {icon}
        <span
          style={{
            fontSize: compact ? '12px' : '12px',
            fontWeight: 800,
            color: 'var(--text-primary)',
          }}
        >
          {text}
        </span>
      </div>
    </GlassContainer>
  );
};

const InfoSection = ({
  title,
  icon,
  children,
}) => {
  return (
    <GlassContainer
      radius={TSizes.radiusXL || 24}
      style={{
        padding: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '14px',
        }}
      >
        <div
          style={{
            width: '34px',
            height: '34px',
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--nav-indicator)',
            color: 'var(--primary)',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>

        <h3
          style={{
            margin: 0,
            fontSize: '15px',
            fontWeight: 900,
            color: 'var(--text-primary)',
          }}
        >
          {title}
        </h3>
      </div>

      {children}
    </GlassContainer>
  );
};

const MetaCard = ({
  icon,
  label,
  value,
}) => {
  return (
    <GlassContainer
      radius={TSizes.radiusXL || 24}
      style={{
        padding: '14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--nav-indicator)',
            color: 'var(--primary)',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: 'var(--text-primary)',
              fontWeight: 900,
              fontSize: '15px',
            }}
          >
            {value}
          </div>

          <div
            style={{
              color: 'var(--text-secondary)',
              fontSize: '12px',
              marginTop: '2px',
              fontWeight: 600,
            }}
          >
            {label}
          </div>
        </div>
      </div>
    </GlassContainer>
  );
};

const BulletItem = ({
  text,
  icon,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
      }}
    >
      <div
        style={{
          width: '26px',
          height: '26px',
          borderRadius: '999px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--nav-indicator)',
          color: 'var(--primary)',
          flexShrink: 0,
          marginTop: '1px',
        }}
      >
        {icon}
      </div>

      <p
        style={{
          margin: 0,
          color: 'var(--text-secondary)',
          lineHeight: 1.7,
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        {text}
      </p>
    </div>
  );
};
